import * as React from 'react';
import { Metadata, Schema, SchemaField } from '../../../types/Metadata';
import { ValidationMode } from '../../../enums/ValidationMode';
import { MetadataFormFieldGroup } from '../MetadataFormFieldGroup/metadataFormFieldGroup';
import { FormUpdater } from '../../../types/Field';
import { AudienceMetadataButtons } from './AudienceMetadataButtons';
import { AdditionalQuestionsModal } from '../AdditionalQuestionsField/additionalQuestionsModal';
import {
  isPublishedOrCompletedInCPP,
  isWIPProductionPhase
} from '../../../utils/status';
import { FieldGroup } from '../../../enums/FieldGroup';
import {
  AudienceMetadataField,
  AudienceMetadataHiddenFields
} from '../../../enums/AudienceMetadataField';
import { ItemType } from '../../../enums/ItemType';
import { getSchemaForm } from '../../../utils';
import { useSelector } from '../../../utils/useSelector';
import { useActions } from '../metadataFormActions';

export type AudienceDetailsGroupProps = {
  data: Partial<Metadata>;
  impliedDataProps?: any;
  schema: Schema;
  schemas?: Schema[];
  disabled?: boolean;
  readonlyFields?: string[];
  updater: FormUpdater;
  shouldValidate?: ValidationMode;
  pushResponse?: any;
  isModal?: boolean;
  field?: SchemaField;
};

export const AudienceDetailsGroup = (props: AudienceDetailsGroupProps) => {
  const [isAllMetadataVisible, setAllMetadataVisible] = React.useState(false);
  const [isAdditionalQuestionsVisible, setAdditionalQuestionsVisible] =
    React.useState(false);
  const [showAdditionalQuestionsModal, setShowAdditionalQuestionsModal] =
    React.useState(false);

  const { updateFields, updateData } = useActions();
  const allErrorFields = useSelector(s => s.form?.allErrorFields);
  const itemType = props.data?.props?.itemType;
  const statusId = props.data?.props?.status?.[0]?.props?.id;

  const fieldsToShow = React.useMemo(() => {
    const fields = [
      AudienceMetadataField.PrimaryQuestion,
      AudienceMetadataField.Function
    ];

    if (isAdditionalQuestionsVisible) {
      fields.push(AudienceMetadataField.AdditionalQuestions);
      fields.push(AudienceMetadataField.AdditionalFunctions);
    }

    return fields;
  }, [isAdditionalQuestionsVisible]);

  const filteredFields = React.useMemo(() => {
    if (itemType !== ItemType.content) {
      return undefined;
    }

    if (isAllMetadataVisible) {
      return undefined;
    }

    const contentSchemaForm = getSchemaForm(props.schema, props.data);
    return contentSchemaForm?.props.fields?.filter(f =>
      fieldsToShow.includes(f.key as AudienceMetadataField)
    ) as SchemaField[];
  }, [props.schema, props.data, itemType, isAllMetadataVisible, fieldsToShow]);

  React.useEffect(() => {
    if (props.pushResponse?.errors?.length > 0) {
      const hasHiddenFieldError = props.pushResponse.errors.some(
        error =>
          AudienceMetadataHiddenFields.includes(error.key) &&
          !fieldsToShow.includes(error.key)
      );

      if (hasHiddenFieldError) {
        setAllMetadataVisible(true);
      }
    }
  }, [props.pushResponse, fieldsToShow]);

  const hasErrors = React.useMemo(() => {
    if (!allErrorFields || allErrorFields.size === 0) return false;
    const contentSchemaForm = getSchemaForm(props.schema, props.data);
    const audienceFields =
      contentSchemaForm?.props.fields?.filter(
        f => f.parentField == FieldGroup.AudienceDetails
      ) || [];

    return audienceFields.some(field => allErrorFields.has(field.key));
  }, [allErrorFields, props.schema, props.data]);

  React.useEffect(() => {
    if (hasErrors) {
      setAllMetadataVisible(true);
    }
  }, [hasErrors]);

  return (
    <>
      <MetadataFormFieldGroup
        {...props}
        fields={filteredFields}
        groupKey={FieldGroup.AudienceDetails}
      >
        {props.field?.field === FieldGroup.AudienceDetails &&
          itemType === ItemType.content && (
            <AudienceMetadataButtons
              isAllMetadataVisible={isAllMetadataVisible}
              setAllMetadataVisible={setAllMetadataVisible}
              isAdditionalQuestionsVisible={isAdditionalQuestionsVisible}
              setAdditionalQuestionsVisible={setAdditionalQuestionsVisible}
              itemType={itemType}
              isModal={props.isModal}
              data={props.data}
              addQuestionsButtonText={
                isPublishedOrCompletedInCPP(statusId) || isWIPProductionPhase(statusId)
                  ? 'Update Audience Metadata'
                  : 'Add More Question(s)'
              }
              onAddAdditionalQuestions={() => {
                if (
                  isPublishedOrCompletedInCPP(statusId) ||
                  isWIPProductionPhase(statusId)
                ) {
                  setShowAdditionalQuestionsModal(true);
                } else {
                  setAdditionalQuestionsVisible(true);
                }
              }}
            />
          )}
      </MetadataFormFieldGroup>
      {showAdditionalQuestionsModal && (
        <AdditionalQuestionsModal
          data={props.data}
          schema={props.schema}
          schemas={props.schemas}
          onClose={() => setShowAdditionalQuestionsModal(false)}
          onSave={async newFields => {
            try {
              // Use f.value directly — the modal initialises each field from the
              // existing data and tracks the full final state, so we replace
              // rather than append (appending would re-add removed items).
              const fieldsToUpdate = newFields.map(f => ({
                field: f.field,
                value: f.value
              }));
              if (fieldsToUpdate.length) {
                await updateFields(fieldsToUpdate);
                await updateData(fieldsToUpdate, undefined);
              }
              setShowAdditionalQuestionsModal(false);
            } catch (e) {
              console.error('Failed to save additional audience', e);
            }
          }}
        />
      )}
    </>
  );
};
